import { Component, ViewEncapsulation, AfterViewInit, ElementRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { FormGroup, AbstractControl, FormBuilder, Validators } from '@angular/forms';
import { CustomValidators } from 'ng2-validation';
import { CommonService } from "../../shared/common/common.service";
import { AuthService } from "../auth.service";
import { OnInit } from '@angular/core';
import { UserService } from '../users/user.service';
import { AuthResponseCode } from '../constants/auth-response-code.enum';
import { jwtDecode } from "jwt-decode";
import { JwtUtils } from '../utilities/jwtUtils';
import { LocalStorageKeys } from '../../core/constants/localStorageKeys.enum';
import { ConsoleLogger } from '../../core/utilities/console-logger';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class LoginComponent implements OnInit, AfterViewInit {
  @ViewChild("usernameField") usernameField : ElementRef;
  @ViewChild("passwordField") passwordField : ElementRef;
  @ViewChild('areaCodeInput') areaCodeInput: ElementRef;
  @ViewChild('exchangeInput') exchangeInput: ElementRef;
  @ViewChild('lineNumberInput') lineNumberInput: ElementRef;

  public form: FormGroup;
  public UserName: AbstractControl;
  public UserPassword: AbstractControl;
  public MfaCode: AbstractControl;
  public NewPhoneNumber: AbstractControl;
  public SmsConsent: AbstractControl;
  public TermsConsent: AbstractControl;
  public isLoading = false;
  public saveUsername = false;
  public errorMessage: string;
  public phoneSuffix: string;

  public showLogin = true;
  public showCode = false;
  public showPhone = false;
  public phoneMask = ['(', /\d/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/];
  constructor(
    private router: Router,
    private fb: FormBuilder,
    private commonService: CommonService,
    private authService: AuthService,
    private userService: UserService
  ) {
    this.form = this.fb.group({
      'UserName': ['', Validators.compose([Validators.required, CustomValidators.UserName])],
      'UserPassword': ['', Validators.compose([Validators.required, Validators.minLength(3)])],
      'MfaCode': [''],
      'NewPhoneNumber': [''],
      'SmsConsent': [false],
      'TermsConsent': [false]
    });

    this.UserName = this.form.controls['UserName'];
    this.UserPassword = this.form.controls['UserPassword'];
    this.MfaCode = this.form.controls['MfaCode'];
    this.NewPhoneNumber = this.form.controls['NewPhoneNumber'];
    this.SmsConsent = this.form.controls['SmsConsent'];
    this.TermsConsent = this.form.controls['TermsConsent'];
  }

  ngOnInit() {
    if (this.authService.isEmployeeLogin) {
      this.router.navigateByUrl("/auth/internal-login");
    }

    this.restoreUsername();

    if (this.authService.isAuthenticated) {
      this.authService.navigateToLandingPage();
    }
  }

  public async onSubmit(): Promise<void> {
    this.errorMessage = '';
    this.authService.clearAuthSession();

    if (this.form.valid) {
      this.isLoading = true;
      document.getElementById('preloader').classList.remove('hide');
      if(!this.form.value.NewPhoneNumber){
        await this.submitLogin();

      } else {
        await this.submitNewPhone();
      }
    }
  }

  private submitLogin = async (): Promise<void> => {
    this.errorMessage = "";
    try {
      const authResponse = await this.authService
        .loginWithUserNamePassword(this.form.value.UserName, this.form.value.UserPassword, this.form.value.MfaCode);

      ConsoleLogger.logInfo(authResponse);

      switch(authResponse.responseCode) {
        case AuthResponseCode.MFA_SETUP_REQUIRED:
          this.showLogin = false;
          this.showCode = false;
          this.showPhone = true;
          break;
        case AuthResponseCode.MFA_CODE_SENT:
          if(authResponse.mfaPhoneSuffix) {
            this.phoneSuffix = authResponse.mfaPhoneSuffix;
          }
          this.showLogin = false;
          this.showCode = true;
          this.showPhone = false;
          break;
        case AuthResponseCode.FORCE_PASSWORD_CHANGE:
          alert("You must change your password before logging in.");
          break;
        case AuthResponseCode.AUTH_SUCCESS:
          if (this.saveUsername) {
            localStorage.setItem(LocalStorageKeys.SAVED_USERNAME, this.UserName.value)
          } else {
            localStorage.removeItem(LocalStorageKeys.SAVED_USERNAME);
          }
          this.authService.navigateToLandingPage();

          break;
        default:
          this.errorMessage = 'An unknown error occurred while logging in.';
          break;
      }
    } catch(err) {
        this.errorMessage = err.error;
    } finally {
      this.isLoading = false;
      document.getElementById('preloader').classList.add('hide');
    };
  }

  private submitNewPhone = async () => {
    try {
      const newPhoneNumber = this.form.value.NewPhoneNumber.replace(/\D/g, '');

      if (newPhoneNumber.length !== 10) {
        this.errorMessage = "Phone number must be a 10 digit number";
        return;
      }
      if (!this.form.value.SmsConsent) {
        this.errorMessage = "Please read the Privacy Policy and check the box to accept.";
        return;
      }
      if (!this.form.value.TermsConsent) {
        this.errorMessage = "Please read the Terms of Service and check the box to accept.";
        return;
      }
      this.phoneSuffix = newPhoneNumber.slice(-4)

      const resp = await this.userService
        .setMfaPhoneNumber(this.form.value.UserName, this.form.value.UserPassword, newPhoneNumber, this.form.value.MfaCode)
        .toPromise();

      switch (resp.responseCode) {
        case AuthResponseCode.MFA_CODE_SENT:
          this.showLogin = false;
          this.showCode = true;
          this.showPhone = false;
          break;
        case AuthResponseCode.MFA_PHONE_VERIFIED:
          try {
            this.showLogin = true;
            this.showCode = false;
            this.showPhone = false;
            this.form.patchValue({
              'MfaCode': '',
              'NewPhoneNumber': ''
            })
            await this.submitLogin();
          } catch(err) {
            this.errorMessage = "Invalid login. \nPlease check your login credentials or contact Aladdin for further assistance.";
          };
      }
    } catch(err) {
      this.errorMessage = err.error;
    } finally {
      document.getElementById('preloader').classList.add('hide');
      this.isLoading = false;
    }
  }

  private restoreUsername() {
    const username = localStorage.getItem(LocalStorageKeys.SAVED_USERNAME);

    if (username) {
      this.UserName.patchValue(username);
      this.saveUsername = true;
      this.passwordField.nativeElement.focus();
      return;
    }

    this.usernameField.nativeElement.focus();
  }

  ngAfterViewInit() {
    document.getElementById('preloader').classList.add('hide');
  }

  public startOver(){
    this.errorMessage = "";
    this.phoneSuffix = "";
    this.showLogin = true;
    this.showCode = false;
    this.showPhone = false;
    this.form.patchValue({
      'MfaCode': '',
      'NewPhoneNumber': '',
      'SmsConsent': false,
      'TermsConsent': false
    });
  }


}



// WEBPACK FOOTER //
// ./src/app/auth/login/login.component.ts